import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import moment from 'moment';
import { Subscription } from 'rxjs';

/**
 * PayBright terminal payment dialog.
 *
 * Unlike the Square terminal flow there is no checkout id to poll: the charge
 * request blocks while the cardholder taps/dips on the terminal and the final
 * result (approved / declined / error) comes back in the single POST response.
 * The dialog therefore shows a "present card on the terminal" state for the
 * duration of that one request and resolves as soon as it returns.
 *
 * Cancel (terminal charges only, like Square's "Cancel Checkout") marks the
 * pending payment Canceled and closes the dialog; staff also press Cancel on
 * the terminal. If the card is tapped anyway the backend records it as Paid.
 */
@Component({
  selector: 'app-paybright-terminal-payment',
  templateUrl: './paybright-terminal-payment.component.html',
})
export class PaybrightTerminalPaymentComponent implements OnInit {
  amount: number;
  currency: string;
  studioId: string;
  enrollmentIds: any[] = [];
  studentAccountId: string | null = null;
  paymentForm: FormGroup | null = null;
  idempotencyKey: string = '';
  savedCardId: string | null = null;
  saveCard: boolean = false;

  paymentStatus: 'PROCESSING' | 'APPROVED' | 'DECLINED' | 'ERROR' | null = null;
  paymentStatusMessage: string = '';
  isProcessing: boolean = false;
  referenceNumber: string | null = null;
  gatewayErrorCode: string | null = null;

  /** The in-flight charge request, dropped when staff cancel. */
  private chargeSubscription: Subscription | null = null;
  isCancelling: boolean = false;

  constructor(
    private drupalRESTService: DrupalRESTService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<PaybrightTerminalPaymentComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.amount = data.amount;
    this.currency = data.currency || 'USD';
    this.studioId = data.studioId;
    this.enrollmentIds = data.enrollmentIds || [];
    this.studentAccountId = data.studentAccountId || null;
    this.paymentForm = data.paymentForm || null;
    this.idempotencyKey = data.idempotencyKey || '';
    this.savedCardId = data.savedCardId || null;
    this.saveCard = !!data.saveCard;
  }

  ngOnInit() {
    if (!this.amount || this.amount <= 0 || !this.studioId) {
      this.snackBar.open('Invalid amount or studio ID provided', 'Close', { duration: 5000 });
      this.dialogRef.close({ status: 'ERROR', message: 'Invalid amount or studio ID' });
      return;
    }
    // Kick off the charge immediately; the terminal prompts the cardholder.
    this.capturePayment();
  }

  onRetryClick() {
    this.capturePayment();
  }

  private capturePayment() {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    this.paymentStatus = 'PROCESSING';
    // A saved-card charge runs card-not-present; only a terminal charge prompts
    // the cardholder to present a card.
    this.paymentStatusMessage = this.savedCardId
      ? 'Charging the saved card…'
      : 'Present the card on the terminal…';
    this.referenceNumber = null;
    this.gatewayErrorCode = null;
    this.isCancelling = false;

    const amountInCents = Math.round(this.amount * 100);

    if (!this.enrollmentIds.length) {
      this.handleError(new Error('No enrollment IDs provided'));
      return;
    }

    const endpoint = `/api/paybright/terminal/charge/${this.studioId}/${amountInCents}/${this.currency}`;

    // Format the payment form data the same way the Square flow and the
    // payments-entity-form do.
    const paymentFormData = { ...this.paymentForm.value };
    if (paymentFormData.field_date_and_time) {
      paymentFormData.field_date_and_time = moment(paymentFormData.field_date_and_time.toString()).format('YYYY-MM-DD[T]HH:mm:ss');
    }

    const payload = {
      enrollmentIds: this.enrollmentIds,
      studentAccountId: this.studentAccountId,
      currency: this.currency,
      amount: amountInCents,
      paymentForm: paymentFormData,
      idempotencyKey: this.idempotencyKey,
      savedCardId: this.savedCardId,
      saveCard: this.saveCard,
    };

    this.chargeSubscription = this.drupalRESTService.httpPOST(endpoint, payload).subscribe({
      next: (response) => this.handleResult(response),
      error: (error) => this.handleError(error),
    });
  }

  onCancelPaymentClick() {
    if (this.isCancelling) {
      return;
    }
    this.isCancelling = true;
    const endpoint = `/api/paybright/terminal/cancel/${this.studioId}`;
    this.drupalRESTService.httpPOST(endpoint, { studentAccountId: this.studentAccountId }).subscribe({
      next: () => {
        // Stop waiting; the backend still records a tap that lands anyway.
        this.chargeSubscription?.unsubscribe();
        this.snackBar.open('Payment canceled. Press Cancel on the terminal too.', 'Close', { duration: 8000 });
        // No result: the parent stays quiet; this snackbar is the message.
        this.dialogRef.close();
      },
      error: () => {
        this.isCancelling = false;
        this.snackBar.open('Could not cancel the payment. Please try again.', 'Close', { duration: 5000 });
      },
    });
  }

  private handleResult(response: any) {
    this.isProcessing = false;

    if (response?.status === 'APPROVED' || response?.status === 'PARTIALLY_APPROVED') {
      this.paymentStatus = 'APPROVED';
      this.referenceNumber = response.reference_number ?? null;
      const card = response.card_type ? `${response.card_type} ••••${response.last_4 ?? ''}` : '';
      const partial = response.status === 'PARTIALLY_APPROVED';
      this.paymentStatusMessage = partial
        ? `Partially approved: ${response.captured_amount} of ${response.requested_amount} captured.${card ? ' \u00b7 ' + card : ''}`
        : `Approved${card ? ' \u00b7 ' + card : ''}.`;
      this.snackBar.open(partial ? 'Card partially approved.' : 'Payment approved.', 'Close', { duration: 5000 });
      // Pass the backend status through unchanged so the parent can warn on a
      // partial approval, surface the reference on the receipt, and refresh the
      // saved-card list when a card was just saved.
      this.dialogRef.close({
        status: response.status,
        referenceNumber: this.referenceNumber,
        // The payment id the backend actually stored, plus card/surcharge
        // details for the receipt.
        payment_id: response.payment_id ?? null,
        requested_amount: response.requested_amount,
        captured_amount: response.captured_amount,
        surcharge: response.surcharge ?? 0,
        last_4: response.last_4 ?? null,
        card_type: response.card_type ?? null,
        saved_card: response.saved_card ?? null,
        error: null,
      });
      return;
    }

    // The backend returns 400 with a status of DECLINED/ERROR for non-approvals,
    // which routes to handleError; this branch covers a 200 with a non-approved
    // body, just in case.
    this.handleError({ error: response });
  }

  private handleError(error: any) {
    this.isProcessing = false;

    const body = error?.error ?? error;
    const backendStatus = body?.status;

    // The charge succeeded at the gateway but the local record failed to save
    // (HTTP 500, status APPROVED_UNRECORDED). Surface it distinctly so the
    // parent does not show a success receipt and staff reconcile manually.
    if (backendStatus === 'APPROVED_UNRECORDED') {
      this.isProcessing = false;
      this.paymentStatus = 'ERROR';
      this.referenceNumber = body?.reference_number ?? null;
      this.paymentStatusMessage = body?.error || 'The card was charged but the payment record could not be saved. Please reconcile manually.';
      this.snackBar.open(this.paymentStatusMessage, 'Close', { duration: 10000 });
      this.dialogRef.close({
        status: 'APPROVED_UNRECORDED',
        referenceNumber: this.referenceNumber,
        error: this.paymentStatusMessage,
      });
      return;
    }

    this.paymentStatus = backendStatus === 'DECLINED' ? 'DECLINED' : 'ERROR';
    this.gatewayErrorCode = body?.error_code != null ? String(body.error_code) : null;

    if (typeof body?.error === 'string') {
      this.paymentStatusMessage = body.error;
    }
    else if (typeof body?.error_message === 'string') {
      this.paymentStatusMessage = body.error_message;
    }
    else if (typeof error?.message === 'string') {
      this.paymentStatusMessage = error.message;
    }
    else {
      this.paymentStatusMessage = this.paymentStatus === 'DECLINED'
        ? 'The card was declined.'
        : 'The payment could not be processed. Please try again.';
    }

    this.snackBar.open(this.paymentStatusMessage, 'Close', { duration: 5000 });
  }

  onClose() {
    this.dialogRef.close({
      status: this.paymentStatus,
      referenceNumber: this.referenceNumber,
      error: (this.paymentStatus === 'ERROR' || this.paymentStatus === 'DECLINED') ? this.paymentStatusMessage : null,
    });
  }

  onCancel() {
    // "Go Back" is only shown after a decline/error the user has already seen in
    // this dialog, so treat it as a quiet dismissal. Resolve CANCELED so the
    // parent does not re-fire a second generic failure toast. (There is no
    // server-side cancel for an in-flight charge; a completed charge is still
    // reconciled via the payment entity / webhooks.)
    this.dialogRef.close({ status: 'CANCELED' });
  }
}
